import { normalizeRelayUrl, uniqueRelayUrls } from "@/utils/url"
import { eventAddress, KINDS, tagValue, type DraftEvent, type NostrEvent, type RelaySet } from "./types"

/** Parses a kind 30002 relay set event (`d` id, `title` name, `relay` tags). */
export function parseRelaySetEvent(event: NostrEvent): RelaySet | undefined {
  if (event.kind !== KINDS.RELAY_SETS) return undefined
  const id = tagValue(event, "d")
  if (!id) return undefined
  const relayUrls = uniqueRelayUrls(
    event.tags.filter((tag) => tag[0] === "relay" || tag[0] === "r").map((tag) => tag[1]),
  )
  return {
    id,
    aTag: ["a", eventAddress(event)],
    name: tagValue(event, "title") || id,
    relayUrls,
  }
}

/**
 * Parses a kind 10012 "favorite relays" event.
 *
 * The event carries both single relays (`relay` tags) and references to relay
 * sets (`a` tags pointing at kind 30002 events), which is how Jumble models
 * "my relay feeds".
 */
export function parseFavoriteRelaysEvent(event: NostrEvent): {
  relayUrls: string[]
  setIds: string[]
} {
  if (event.kind !== KINDS.FAVORITE_RELAYS) return { relayUrls: [], setIds: [] }
  const relayUrls: string[] = []
  const setIds: string[] = []
  for (const tag of event.tags) {
    if (tag[0] === "relay" || tag[0] === "r") {
      const url = normalizeRelayUrl(tag[1])
      if (url && !relayUrls.includes(url)) relayUrls.push(url)
      continue
    }
    if (tag[0] === "a" && typeof tag[1] === "string") {
      const [kind, , identifier] = tag[1].split(":")
      if (Number(kind) !== KINDS.RELAY_SETS || !identifier) continue
      if (!setIds.includes(identifier)) setIds.push(identifier)
    }
  }
  return { relayUrls, setIds }
}

export function buildRelaySetDraft(set: RelaySet, previousContent = ""): DraftEvent {
  return {
    kind: KINDS.RELAY_SETS,
    // Keep any encrypted payload written by another client instead of dropping it.
    content: previousContent,
    tags: [
      ["d", set.id],
      ["title", set.name],
      ...set.relayUrls.map((url) => ["relay", url]),
    ],
    created_at: Math.floor(Date.now() / 1000),
  }
}

export function buildFavoriteRelaysDraft(
  relayUrls: string[],
  relaySetTags: string[][],
  previousContent = "",
): DraftEvent {
  return {
    kind: KINDS.FAVORITE_RELAYS,
    content: previousContent,
    tags: [...relayUrls.map((url) => ["relay", url]), ...relaySetTags],
    created_at: Math.floor(Date.now() / 1000),
  }
}

/** `["a", "30002:pubkey:id"]` -> `id` */
export function relaySetIdFromATag(tag: string[] | undefined): string | undefined {
  if (!tag || tag[0] !== "a" || typeof tag[1] !== "string") return undefined
  const [kind, , identifier] = tag[1].split(":")
  if (Number(kind) !== KINDS.RELAY_SETS) return undefined
  return identifier || undefined
}

export function relaySetATag(pubkey: string, id: string): string[] {
  return ["a", `${KINDS.RELAY_SETS}:${pubkey}:${id}`]
}

export function newRelaySetId(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
  const suffix = Math.random().toString(36).slice(2, 8)
  return slug ? `${slug}-${suffix}` : `set-${suffix}`
}
